import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './LegalPage.css';
import './TrialPage.css';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'YOUR_SITE_KEY_HERE';

// Public axios instance — χωρίς /api prefix, χωρίς Keycloak token
const publicApi = axios.create({
  baseURL: process.env.REACT_APP_PUBLIC_API_URL || '',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: false,
});

export default function TrialPage() {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [form, setForm] = useState({
    name:      '',
    salonName: '',
    email:     '',
    phone:     '',
    city:      '',
    staff:     '1',
    message:   '',
  });
  const [captchaToken, setCaptchaToken] = useState('');
  const [loading, setLoading]           = useState(false);
  const [success, setSuccess]           = useState(false);
  const [error, setError]               = useState('');

  useEffect(() => {
    let script = document.querySelector('script[src*="recaptcha/api.js"]');
    if (!script) {
      script = document.createElement('script');
      script.src   = 'https://www.google.com/recaptcha/api.js';
      script.async = true;
      script.defer = true;
      script.id    = 'recaptcha-script';
      document.head.appendChild(script);
    }
    window.onRecaptchaSuccess = (token) => setCaptchaToken(token);
    window.onRecaptchaExpired = ()      => setCaptchaToken('');
    return () => {
      delete window.onRecaptchaSuccess;
      delete window.onRecaptchaExpired;
      const s = document.getElementById('recaptcha-script');
      if (s) s.remove();
      document.querySelectorAll('.grecaptcha-badge, iframe[src*="recaptcha"]').forEach(el => el.remove());
      delete window.grecaptcha;
    };
  }, []);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!captchaToken) { setError('Παρακαλώ επιβεβαιώστε ότι δεν είστε ρομπότ.'); return; }
    setLoading(true); setError('');
    try {
      await publicApi.post('/trial', { ...form, captchaToken });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Σφάλμα αποστολής. Δοκιμάστε ξανά.');
      if (window.grecaptcha) window.grecaptcha.reset();
      setCaptchaToken('');
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="legal-root">
        <header className="legal-header">
          <button className="legal-back" onClick={() => navigate('/')}>← Επιστροφή</button>
          <div className="legal-logo">✂ SalonBook</div>
        </header>
        <div className="trial-success">
          <div className="trial-success-icon">🎉</div>
          <h1>Λάβαμε την αίτησή σας!</h1>
          <p>Θα επικοινωνήσουμε μαζί σας στο <strong>{form.email}</strong> εντός 24 ωρών.</p>
          <button className="trial-back-btn" onClick={() => navigate('/')}>← Επιστροφή στην αρχική</button>
        </div>
        <footer className="legal-footer">
          <span>© {new Date().getFullYear()} SalonBook.</span>
          <div className="legal-footer-links">
            <a href="/privacy">Πολιτική Απορρήτου</a>
            <a href="/terms">Όροι Χρήσης</a>
            <a href="/gdpr">GDPR</a>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="legal-root">
      <header className="legal-header">
        <button className="legal-back" onClick={() => navigate('/')}>← Επιστροφή</button>
        <div className="legal-logo">✂ SalonBook</div>
      </header>

      <div className="trial-layout">
        {/* ── Left info ── */}
        <div className="trial-info">
          <div className="legal-badge">Δωρεάν δοκιμή 14 ημερών</div>
          <h1>Ξεκίνα σήμερα<br />χωρίς κόστος</h1>
          <p className="trial-lead">Δεν χρειάζεται πιστωτική κάρτα. Ακύρωση οποτεδήποτε.</p>

          <ul className="trial-perks">
            {[
              'Πλήρης πρόσβαση σε όλα τα χαρακτηριστικά',
              'Ημερολόγιο κρατήσεων χωρίς όρια',
              'Διαχείριση υπαλλήλων και πελατών',
              'Στατιστικά και αναφορές εσόδων',
              'Υποστήριξη μέσω email',
              'Εγκατάσταση και εκπαίδευση δωρεάν',
            ].map((p, i) => (
              <li key={i}><span className="trial-perk-icon">✅</span> {p}</li>
            ))}
          </ul>

          <div className="trial-quote">
            <div className="trial-quote-text">«Το SalonBook άλλαξε τον τρόπο που διαχειρίζομαι το κομμωτήριό μου. Εξοικονομώ 2 ώρες την ημέρα!»</div>
            <div className="trial-quote-author">— Μαρία Κ., Κομμωτήριο Αθήνα</div>
          </div>
        </div>

        {/* ── Right form ── */}
        <div className="trial-form-wrap">
          <div className="trial-form-card">
            <h2>Συμπληρώστε τα στοιχεία σας</h2>
            <p>Θα επικοινωνήσουμε μαζί σας εντός 24 ωρών.</p>

            {error && <div className="trial-error">{error}</div>}

            <div className="trial-form">
              <div className="trial-field">
                <label>Ονοματεπώνυμο *</label>
                <input type="text" placeholder="π.χ. Μαρία Παπαδοπούλου" required
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>

              <div className="trial-field">
                <label>Όνομα κομμωτηρίου *</label>
                <input type="text" placeholder="π.χ. Studio Maria" required
                  value={form.salonName} onChange={e => setForm(f => ({ ...f, salonName: e.target.value }))} />
              </div>

              <div className="trial-row">
                <div className="trial-field">
                  <label>Email *</label>
                  <input type="email" placeholder="email@example.com" required
                    value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="trial-field">
                  <label>Τηλέφωνο *</label>
                  <input type="tel" placeholder="69ΧΧΧΧΧΧΧΧ" required
                    value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
              </div>

              <div className="trial-row">
                <div className="trial-field">
                  <label>Πόλη</label>
                  <input type="text" placeholder="π.χ. Αθήνα"
                    value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                </div>
                <div className="trial-field">
                  <label>Αριθμός υπαλλήλων</label>
                  <select value={form.staff} onChange={e => setForm(f => ({ ...f, staff: e.target.value }))}>
                    <option value="1">Μόνος/η μου</option>
                    <option value="2-3">2-3 υπάλληλοι</option>
                    <option value="4-6">4-6 υπάλληλοι</option>
                    <option value="7+">7+ υπάλληλοι</option>
                  </select>
                </div>
              </div>

              <div className="trial-field">
                <label>Μήνυμα (προαιρετικό)</label>
                <textarea placeholder="Πείτε μας λίγα λόγια για το κομμωτήριό σας..." rows={3}
                  value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
              </div>

              {/* reCAPTCHA v2 */}
              <div className="trial-captcha">
                <div
                  ref={recaptchaRef}
                  className="g-recaptcha"
                  data-sitekey={RECAPTCHA_SITE_KEY}
                  data-callback="onRecaptchaSuccess"
                  data-expired-callback="onRecaptchaExpired"
                />
              </div>

              <button
                type="button"
                className="trial-submit"
                disabled={loading || !captchaToken || !form.name || !form.salonName || !form.email || !form.phone}
                onClick={handleSubmit}
              >
                {loading ? 'Αποστολή...' : '🚀 Ξεκίνα τη δωρεάν δοκιμή'}
              </button>

              <p className="trial-terms">
                Με την εγγραφή αποδέχεστε τους <a href="/terms">Όρους Χρήσης</a> και την{' '}
                <a href="/privacy">Πολιτική Απορρήτου</a>.
              </p>
            </div>
          </div>
        </div>
      </div>

      <footer className="legal-footer">
        <span>© {new Date().getFullYear()} SalonBook. Όλα τα δικαιώματα διατηρούνται.</span>
        <div className="legal-footer-links">
          <a href="/privacy">Πολιτική Απορρήτου</a>
          <a href="/terms">Όροι Χρήσης</a>
          <a href="/gdpr">GDPR</a>
        </div>
      </footer>
    </div>
  );
}